import {ERROR, UNIT} from '@di-shared/const';
import environmentStore from '@di-shared/features/environment/environmentStore';
import converterBuilder from '@di-shared/utils/converterBuilder';
import {DistanceUnitType, RegionType} from '@shared/cci/features/const/environment';

const PER_LITER = '/l';
const PER_KWH = '/kWh';
const PER_HOUR = '/h';

export enum DistanceUnitSymbol {
  M = 'm',
  KM = 'km',
  YARD = 'yd',
  FEET = 'ft',
  MILE = 'mi',
}

const DefaultDistanceUnitSymbol = {
  [DistanceUnitType.KM]: DistanceUnitSymbol.KM,
  [DistanceUnitType.MILE_FEET]: DistanceUnitSymbol.MILE,
  [DistanceUnitType.MILE_YARD]: DistanceUnitSymbol.MILE,
};

const conversionRates: {[key in DistanceUnitSymbol]: number} = {
  [DistanceUnitSymbol.M]: 1000, // 1 km = 1000 m
  [DistanceUnitSymbol.KM]: 1, // 기본 단위는 km
  [DistanceUnitSymbol.FEET]: 3280.84, // 1 km = 3280.84 feet
  [DistanceUnitSymbol.YARD]: 1093.613, // 1 km = 1093.613 yard
  [DistanceUnitSymbol.MILE]: 0.621371, // 1 km = 0.621 mile
};

function convertUnits(value: number, fromUnit: DistanceUnitSymbol, toUnit: DistanceUnitSymbol): number {
  const valueInKm = value / conversionRates[fromUnit];
  return valueInKm * conversionRates[toUnit];
}

function mKmFormatter(kmValue: number) {
  /**
   * DI 서버 기본단위가 km이기 때문에 kmValue로 들어옴
   * 함수이름은 임시로..
   * m/km 단위인 경우
   * 0m == 거리인 경우, 0m 로 표시
   * 0m < 거리 < 8m인 경우, 5m로 표시
   * 8m <= 거리 <= 10m인 경우, 10m로 표시
   * 10m < 거리 < 995m인 경우, 1자리는 버림하며 10m단위로 표시 (ex. 10, 20, 990)
   * 996m <= 거리 < 100km인 경우, 소수점 둘째자리에서 반올림하여 첫재짜리까지만 km단위로 표시 (ex. 1.0km, 1.1km, 99.9km)
   * 100km <= 거리인 경우, 소수점 첫째자리에서 반올림하여 일의 자리까지만 km단위로 표시 (ex. 100km, 101km)
   */
  const negativeCorrection = kmValue < 0 ? -1 : 1;
  const mValue = Math.abs(convertUnits(kmValue, DistanceUnitSymbol.KM, DistanceUnitSymbol.M));
  if (mValue <= 0) {
    return {value: 0, symbol: DistanceUnitSymbol.KM};
  }
  if (mValue < 8) {
    return {value: negativeCorrection * 5, symbol: DistanceUnitSymbol.M};
  }
  if (mValue <= 10) {
    return {value: negativeCorrection * 10, symbol: DistanceUnitSymbol.M};
  }
  if (mValue < 995) {
    return {value: negativeCorrection * Math.floor(mValue / 10) * 10, symbol: DistanceUnitSymbol.M};
  }
  if (mValue < 100000) {
    return {value: (negativeCorrection * Math.round(kmValue * 10)) / 10, symbol: DistanceUnitSymbol.KM};
  }
  return {value: negativeCorrection * Math.round(kmValue), symbol: DistanceUnitSymbol.KM};
}

/**
 * km → yard  : meter * 1093.613
 * km → feet : meter * 3280.84
 */
function mileYardFormatter(kmValue: number) {
  /**
   * mile/yard 단위인 경우
   * 1mile = 1760yd
   *
   * 0yd == 거리인 경우, 0yd 로 표시
   * 0yd < 거리 < 175yd인 경우, yd 단위 숫자에서 1의 자리에서 반올림하여 10yd 단위로 표시 (ex. 0yd, 10yd, 20y, 170yd)
   * 175yd ≤ 거리 < 99.95mi(=175912yd)인 경우, mi 단위 숫자에서 소수점 둘째 자리에서 반올림하여 0.1mi 단위로 표시 (ex. 0.2mi, 0.3mi, 99.9mi)
   * 99.95mi ≤ 거리인 경우, mi 단위 숫자에서 소수점 첫째 자리에서 반올림하여 1mi 단위로 표시 (ex. 100mi, 101mi 102mi)
   */
  const negativeCorrection = kmValue < 0 ? -1 : 1;
  const ydValue = Math.abs(convertUnits(kmValue, DistanceUnitSymbol.KM, DistanceUnitSymbol.YARD));
  const miValue = Math.abs(convertUnits(kmValue, DistanceUnitSymbol.KM, DistanceUnitSymbol.MILE));
  if (ydValue <= 0) {
    return {value: 0, symbol: DistanceUnitSymbol.MILE};
  }
  if (ydValue < 175) {
    return {value: negativeCorrection * Math.round(ydValue / 10) * 10, symbol: DistanceUnitSymbol.YARD};
  }
  if (ydValue < 175912) {
    return {value: (negativeCorrection * Math.round(miValue * 10)) / 10, symbol: DistanceUnitSymbol.MILE};
  }
  return {value: negativeCorrection * Math.round(miValue), symbol: DistanceUnitSymbol.MILE};
}

function mileFeetFormatter(kmValue: number) {
  /**
   * mile/feet 단위인  경우
   * 1mile = 5280 feet
   *
   * 0ft == 거리인 경우, 0ft 로 표시
   * 0ft ≤ 거리 < 525ft인 경우, ft단위 숫자에서 1의 자리 반올림하여 10ft 단위로 표시 (ex. 10ft, 20,ft 520ft)
   * 525ft ≤ 거리 < 99.95mi(=527736feet)인 경우, mi 단위 숫자에서 소수점 둘째 자리에서 반올림하여 0.1mi 단위로 표시 (ex. 0.1 mi, 0.2mi, 99.9mi)
   * 99.95mi ≤ 거리인 경우, mi 단위 숫자에서 소수점 첫째 자리에서 반올림하여 1mi 단위로 표시 (ex. 100mi, 101mi 102mi)
   */
  const negativeCorrection = kmValue < 0 ? -1 : 1;
  const ftValue = Math.abs(convertUnits(kmValue, DistanceUnitSymbol.KM, DistanceUnitSymbol.FEET));
  const miValue = Math.abs(convertUnits(kmValue, DistanceUnitSymbol.KM, DistanceUnitSymbol.MILE));
  if (ftValue <= 0) {
    return {value: 0, symbol: DistanceUnitSymbol.MILE};
  }
  if (ftValue < 525) {
    return {value: negativeCorrection * Math.round(ftValue / 10) * 10, symbol: DistanceUnitSymbol.FEET};
  }
  if (ftValue < 527736) {
    return {value: (negativeCorrection * Math.round(miValue * 10)) / 10, symbol: DistanceUnitSymbol.MILE};
  }
  return {value: negativeCorrection * Math.round(miValue), symbol: DistanceUnitSymbol.MILE};
}

function useUnitConverter() {
  const {speedUnit: appSpeedUnitName, distanceUnit: appDistanceUnitName, region} = environmentStore.getState();

  /**
   * 서버에서 오는 기본 값이 km/h 라고 가정
   * addUnit에 들어가는 변환값들은 km/h -> ??? 단위로 변환하는 함수
   */

  const defaultDistanceSymbol = DefaultDistanceUnitSymbol[appDistanceUnitName];
  const distanceConverter = converterBuilder()
    .addUnit(
      DistanceUnitType.KM,
      (kmvalue) => mKmFormatter(kmvalue).symbol,
      (kmvalue) => mKmFormatter(kmvalue).value
    )
    .addUnit(
      DistanceUnitType.MILE_FEET,
      (kmValue: number) => mileFeetFormatter(kmValue).symbol,
      (kmValue: number) => mileFeetFormatter(kmValue).value
    )
    .addUnit(
      DistanceUnitType.MILE_YARD,
      (kmValue: number) => mileYardFormatter(kmValue).symbol,
      (kmValue: number) => mileYardFormatter(kmValue).value
    )
    .build();

  const distanceReverter = converterBuilder()
    .addUnit(
      DistanceUnitType.KM,
      () => DistanceUnitType.KM,
      (kmvalue) => kmvalue
    )
    .addUnit(
      DistanceUnitType.MILE_FEET,
      () => DistanceUnitSymbol.MILE,
      (mileValue) => convertUnits(mileValue, DistanceUnitSymbol.MILE, DistanceUnitSymbol.KM)
    )
    .addUnit(
      DistanceUnitType.MILE_YARD,
      () => DistanceUnitSymbol.MILE,
      (mileValue) => convertUnits(mileValue, DistanceUnitSymbol.MILE, DistanceUnitSymbol.KM)
    )
    .build();

  const simpleDistanceConverter = converterBuilder()
    .addUnit(
      DistanceUnitType.KM,
      () => DistanceUnitSymbol.KM,
      (kmValue) => Math.round(kmValue * 10) / 10
    )
    .addUnit(
      DistanceUnitType.MILE_FEET,
      () => DistanceUnitSymbol.MILE,
      (kmValue) => convertUnits(kmValue, DistanceUnitSymbol.KM, DistanceUnitSymbol.MILE)
    )
    .addUnit(
      DistanceUnitType.MILE_YARD,
      () => DistanceUnitSymbol.MILE,
      (kmValue) => convertUnits(kmValue, DistanceUnitSymbol.KM, DistanceUnitSymbol.MILE)
    )
    .build();

  const defaultFuelEfficiencySymbol = defaultDistanceSymbol + PER_LITER;
  const defaultSpeedSymbol = defaultDistanceSymbol + PER_HOUR;

  /**
   * [2025-03-24:Jeyoung Lee] FIXME: EU 앱 단위 설정 나오면 바꿀것
   */
  const defaultEnergyEfficiencySymbolKR = defaultDistanceSymbol + PER_KWH;
  const defaultEnergyEfficiencySymbolEU = `${UNIT.KWH}/100${UNIT.KM}`;

  function convertEnergyEfficiencyValueKR(
    value: number | undefined | null,
    option: {
      skipFormatting?: boolean;
    } = {skipFormatting: false}
  ) {
    if (option.skipFormatting) {
      return value || value === 0 ? simpleDistanceConverter.to(appDistanceUnitName, value).value : ERROR.DASH;
    }
    return value || value === 0 ? distanceConverter.to(appDistanceUnitName, value).value : ERROR.DASH;
  }

  function convertEnergyEfficiencySymbolKR(
    value: number | undefined | null,
    option: {
      skipFormatting?: boolean;
    } = {skipFormatting: false}
  ) {
    if (option.skipFormatting) {
      return defaultEnergyEfficiencySymbolKR;
    }
    return value || value === 0 ? distanceConverter.to(appDistanceUnitName, value).symbol + PER_KWH : defaultEnergyEfficiencySymbolKR;
  }

  function convertEnergyEfficiencyValueEU(value: number | undefined | null) {
    function truncateToSecondDecimal(num: number) {
      return Math.floor(num * 100) / 100;
    }

    return value ? truncateToSecondDecimal(100 / value) : value === 0 ? 0 : ERROR.DASH;
  }

  function convertEnergyEfficiencySymbolEU() {
    return defaultEnergyEfficiencySymbolEU;
  }

  return {
    convertDistanceValue: (value: number | undefined | null) =>
      value || value === 0 ? distanceConverter.to(appDistanceUnitName, value).value : ERROR.DASH,
    convertDistanceSymbol: (value: number | undefined | null) =>
      value || value === 0 ? distanceConverter.to(appDistanceUnitName, value).symbol : defaultDistanceSymbol,
    defaultDistanceSymbol,
    revertDistanceValue: (value: number) => distanceReverter.to(DistanceUnitType.KM, value).value,

    convertFuelEfficiencyValue: (value: number | undefined | null) =>
      value || value === 0 ? distanceConverter.to(appDistanceUnitName, value).value : ERROR.DASH,
    convertFuelEfficiencySymbol: (value: number | undefined | null) =>
      value || value === 0 ? distanceConverter.to(appDistanceUnitName, value).symbol + PER_LITER : defaultFuelEfficiencySymbol,
    defaultFuelEfficiencySymbol,

    convertEnergyEfficiencyValue: region === RegionType.KR ? convertEnergyEfficiencyValueKR : convertEnergyEfficiencyValueEU,
    convertEnergyEfficiencySymbol: region === RegionType.KR ? convertEnergyEfficiencySymbolKR : convertEnergyEfficiencySymbolEU,
    defaultEnergyEfficiencySymbol: region === RegionType.KR ? defaultEnergyEfficiencySymbolKR : defaultEnergyEfficiencySymbolEU,

    convertSpeedValue: (value: number | undefined | null) =>
      value || value === 0 ? distanceConverter.to(appDistanceUnitName, value).value : ERROR.DASH,
    convertSpeedSymbol: (value: number | undefined | null) =>
      value || value === 0 ? distanceConverter.to(appDistanceUnitName, value).symbol + PER_HOUR : defaultSpeedSymbol,
    defaultSpeedSymbol,
  };
}

export default useUnitConverter;
